import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { createUserWithEmailAndPassword, deleteUser, sendEmailVerification, signOut, updateProfile, type User } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { api } from '@/lib/api';

export default function ConsultancyRegister() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    consultancyName: '',
    yourName: '',
    email: '',
    phone: '',
    city: '',
    country: 'India',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please check both password fields.');
      setFormState('error');
      return;
    }
    if (!/(?=.*[A-Z])(?=.*\d).{8,}/.test(formData.password)) {
      setError('Use at least 8 characters with one uppercase letter and one number.');
      setFormState('error');
      return;
    }
    setError('');
    setFormState('loading');
    let createdUser: User | null = null;
    try {
      const credential = await createUserWithEmailAndPassword(firebaseAuth, formData.email.trim(), formData.password);
      createdUser = credential.user;
      await updateProfile(credential.user, { displayName: formData.yourName.trim() });
      await api.provisionConsultancy({ consultancyName: formData.consultancyName, fullName: formData.yourName, phone: formData.phone, city: formData.city, country: formData.country });
      await sendEmailVerification(credential.user);
      await signOut(firebaseAuth);
      navigate('/consultancy/login', { replace: true, state: { registered: true } });
    } catch {
      if (createdUser) await deleteUser(createdUser).catch(() => undefined);
      setError('Account creation failed. The email may already be registered or the password does not meet the Firebase policy.');
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-navy-800">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-700 via-navy-800 to-navy-900" />
        <div className="absolute inset-0">
          {Array.from({ length: 18 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-pulse"
              style={{
                left: `${(i * 37) % 100}%`,
                top: `${(i * 53) % 100}%`,
                animationDelay: `${(i % 5) * 0.6}s`,
              }}
            />
          ))}
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 w-full">
          <img src="/assets/logo2.png" alt="EzyAbroad" className="h-11 w-auto mb-8" />
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            Start growing your
            <br />
            consultancy today.
          </h2>
          <p className="mt-4 text-white/70 text-base leading-relaxed max-w-md">
            Join 500+ consultancies already using EzyAbroad to manage students, track leads, and grow their business.
          </p>
          <div className="mt-10 space-y-4 max-w-md">
            {[
              { icon: 'ri-rocket-2-line', label: '7-day free trial — no card required' },
              { icon: 'ri-team-line', label: 'Manage your team & counselors' },
              { icon: 'ri-file-list-3-line', label: 'Applications & document tracking' },
              { icon: 'ri-passport-line', label: 'AI-powered visa guidance' },
              { icon: 'ri-bar-chart-2-line', label: 'Reports & analytics' },
            ].map((feature) => (
              <div key={feature.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gold-400 flex-shrink-0">
                  <i className={`${feature.icon} text-lg`} />
                </div>
                <span className="text-white/90 text-sm md:text-base">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 md:px-12 py-12 bg-white overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src="/assets/logo2-dark.png" alt="EzyAbroad" className="h-9 w-auto mx-auto" />
            <div className="text-xs text-gray-400 italic mt-1">Consultancy Portal</div>
          </div>
          <div className="lg:block hidden mb-6">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Consultancy Portal</div>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-navy-600">Create Your Free Account</h1>
          <p className="mt-2 text-sm text-gray-500">Start your 14-day free trial. No credit card required.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {formState === 'error' && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Consultancy Name</label>
              <input
                type="text"
                value={formData.consultancyName}
                onChange={(e) => setFormData({ ...formData, consultancyName: e.target.value })}
                placeholder="e.g. Global Dreams Overseas"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Your Name</label>
              <input
                type="text"
                value={formData.yourName}
                onChange={(e) => setFormData({ ...formData, yourName: e.target.value })}
                placeholder="e.g. Rajesh Sharma"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy-600 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@consultancy.com"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-600 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy-600 mb-1.5">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g. Hyderabad"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-600 mb-1.5">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors bg-white"
                >
                  <option value="India">India</option>
                  <option value="Nepal">Nepal</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 characters, 1 uppercase, 1 number"
                  required
                  minLength={8}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-600 mb-1.5">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Must match password"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreeTerms}
                onChange={(e) => setFormData({ ...formData, agreeTerms: e.target.checked })}
                required
                className="w-4 h-4 mt-0.5 rounded border-gray-300 text-gold-500 focus:ring-gold-400"
              />
              <span className="text-sm text-gray-500">
                I agree to the{" "}
                <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }} className="text-gold-600 hover:text-gold-500 cursor-pointer">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }} className="text-gold-600 hover:text-gold-500 cursor-pointer">Privacy Policy</a>
              </span>
            </label>

            <button
              type="submit"
              disabled={formState === 'loading'}
              className="w-full py-3.5 bg-navy-600 text-white font-semibold rounded-lg hover:bg-navy-700 transition-all disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer"
            >
              {formState === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create My Free Account
                  <i className="ri-arrow-right-line" />
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{" "}
            <a
              href="/consultancy/login"
              onClick={(e) => { e.preventDefault(); navigate('/consultancy/login'); }}
              className="text-gold-600 hover:text-gold-500 font-medium cursor-pointer"
            >
              Sign in →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
